import React from "react";
import { useGlobalQuery } from "@hooks";
import { Button } from "primereact/button";
import { useWatch } from "react-hook-form";
import { ArrayUtils } from "@nubeteck/utils";
import { EmptyMessage } from "@nubeteck/prime";
import { TabView, TabPanel } from "primereact/tabview";
import { Form, Dialog, Dropdown, InputSwitch, InputNumber } from "@components";

import {
  IDataSourceField,
  IDashboardWidgetDialogProps,
} from "../../interfaces";

type Format = {
  formatType?: string;
};

type FormValues = {
  title: string;
  subtitle: string;
  FuenteId: null | number;
  CamposId: (IDataSourceField & Format)[];
};

const DashboardTableColumnDialog = ({
  form,
  onSubmitClick,
  ...props
}: IDashboardWidgetDialogProps<FormValues>) => {
  const currencies = useGlobalQuery("Currencies", "getAll", undefined, {
    enabled: props.visible,
  });

  const fields = useWatch({ name: "CamposId", control: form.control });

  const formatTypesOptions = React.useMemo(
    () => [
      { value: "number", label: "Numero (.,)" },
      { value: "currency", label: "Moneda ($)" },
      { value: "percentage", label: "Porcentaje (%)" },
    ],
    [],
  );

  const formatCurrenciesOptions = React.useMemo(() => {
    if (!currencies.data?.length) return [];
    return ArrayUtils.selectLabelValue(
      currencies.data,
      (currency) => `${currency.MonedaNombre} (${currency.Simbolo})`,
      "MonedaId",
    );
  }, [currencies.data]);

  const formatLocalesOptions = React.useMemo(() => {
    if (!currencies.data?.length) return [];
    return ArrayUtils.selectLabelValue(currencies.data, "Locale", "Locale");
  }, [currencies.data]);

  return (
    <Dialog
      {...props}
      title="Formato de columnas"
      className="w-full md:w-10 lg:w-6"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            size="small"
            type="button"
            severity="warning"
            label="Reiniciar formulario"
            onClick={() => {
              form.reset();
            }}
          />
          <Button
            size="small"
            severity="success"
            label="Guardar cambios"
            onClick={form.handleSubmit(onSubmitClick)}
          />
        </div>
      }
    >
      <Form form={form}>
        {fields?.length ? (
          <TabView className="p-custom-tabview">
            {fields.map((field, index) => {
              const prefix = `CamposId.${index}`;
              return (
                <TabPanel key={field.Id} header={field.Label}>
                  <Dropdown
                    defaultValue="number"
                    label="Tipo de formato"
                    className="p-inputtext-sm"
                    options={formatTypesOptions}
                    name={`${prefix}.formatType`}
                    placeholder="Selecciona un formato"
                    helperText="Este formato sera dado al valor cuando se muestre."
                  />
                  {field?.formatType === "currency" && (
                    <Dropdown
                      className="p-inputtext-sm"
                      label="Formato de la moneda"
                      name={`${prefix}.formatCurrency`}
                      options={formatCurrenciesOptions}
                      placeholder="Selecciona una moneda"
                      helperText="Esta moneda sera dado al valor cuando se muestre."
                    />
                  )}
                  <InputNumber
                    max={4}
                    min={0}
                    defaultValue={2}
                    className="p-inputtext-sm"
                    label="Precisión del valor"
                    name={`${prefix}.formatPrecision`}
                    placeholder="Ingresa una precisión"
                  />
                  {field?.formatType !== "currency" && (
                    <Dropdown
                      label="Localización"
                      className="p-inputtext-sm"
                      options={formatLocalesOptions}
                      name={`${prefix}.formatLocale`}
                      placeholder="Selecciona una localización"
                      helperText="Solo aplica cuando el tipo de formato no es moneda."
                    />
                  )}
                  <InputSwitch
                    label="¿Abreviar valor?"
                    name={`${prefix}.formatAbbreviate`}
                    helperText="Convertirá los números grandes a un formato abreviado."
                  />
                </TabPanel>
              );
            })}
          </TabView>
        ) : (
          <EmptyMessage message="No hay columnas seleccionadas en la fuente de datos." />
        )}
      </Form>
    </Dialog>
  );
};

export default DashboardTableColumnDialog;
